import React, { useMemo, useCallback, PropsWithChildren } from 'react';
import { Checkbox, Tooltip, Box } from '@landr/maestro';
import { t } from '@lingui/macro';
import { useSelectedSamplesContext } from 'contexts/SelectedSamplesContext/SelectedSamplesContext';
import { PackState, Sample, SampleState } from 'types/generated/graphql';
import { useDownloadsPageMatch } from 'hooks/useDownloadsPageMatch/useDownloadsPageMatch';

const CheckboxWrapper: React.FC<
    PropsWithChildren<{ isDisabled?: boolean }>
> = ({ isDisabled = false, children }) =>
    isDisabled ? (
        <Tooltip label={t`Feature is unavailable`}>
            <Box data-testid="tooltip">{children}</Box>
        </Tooltip>
    ) : (
        <>{children}</>
    );

export interface SelectAllCheckboxProps {
    samples: Sample[];
}

export const SelectAllCheckbox: React.FC<SelectAllCheckboxProps> = ({
    samples,
}) => {
    const {
        areAllSamplesSelected,
        areSomeSamplesSelected,
        toggleAllSamplesSelection,
        dropSamplesSelection,
    } = useSelectedSamplesContext();

    const { isDownloadsPage } = useDownloadsPageMatch();

    const currentSelectedSamples = useMemo(
        () => ({
            areSomeSelected: areSomeSamplesSelected(samples),
            areAllSelected: areAllSamplesSelected(samples),
        }),
        [areSomeSamplesSelected, samples, areAllSamplesSelected],
    );

    const selectAllSamples = useCallback(() => {
        if (currentSelectedSamples.areSomeSelected) {
            dropSamplesSelection(samples);
        } else {
            toggleAllSamplesSelection(samples);
        }
    }, [
        currentSelectedSamples.areSomeSelected,
        dropSamplesSelection,
        samples,
        toggleAllSamplesSelection,
    ]);

    const areAllSamplesTakenDown = useMemo(() => {
        if (isDownloadsPage) {
            return samples.every(
                (sample) =>
                    sample.sampleState === SampleState.TakenDown ||
                    sample.pack?.packState === PackState.TakenDown,
            );
        }

        return false;
    }, [isDownloadsPage, samples]);

    return (
        <CheckboxWrapper isDisabled={areAllSamplesTakenDown}>
            <Checkbox
                onChange={selectAllSamples}
                isIndeterminate={
                    currentSelectedSamples.areSomeSelected &&
                    !currentSelectedSamples.areAllSelected
                }
                isChecked={currentSelectedSamples.areAllSelected}
                data-testid="select-all-checkbox"
                label=""
                hideLabel
                isDisabled={areAllSamplesTakenDown}
                data-disabled={areAllSamplesTakenDown}
                size="sm"
                marginTop="xs"
            />
        </CheckboxWrapper>
    );
};
